import { useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { setSidekickActingOrganizationId } from '../api/actingOrganization';
import { resetChat } from '../state/aiSidekickChatSlice';

/**
 * Keeps the transport's acting organization in step with the host, and
 * starts a new conversation whenever it changes.
 *
 * The reset is the segregation guarantee. A conversation is one thread
 * on the backend checkpointer, and its turns are stored against the
 * organization each was issued for, so carrying a thread across a
 * switch would interleave two organizations' turns in one history.
 * Resetting drops the conversation id, so the next turn mints a fresh
 * one under the organization now in scope.
 *
 * Deliberately does not reset on first mount: a host can seed a message
 * into the store before the shell mounts, and that would discard it.
 */
export const useSidekickActingOrganization = (
  actingOrganizationId: string | undefined
): void => {
  const dispatch = useDispatch();
  const previousOrganizationId = useRef<string | undefined>(
    actingOrganizationId
  );

  // One effect rather than two, because the scope and the reset are
  // halves of one switch. The browser paints between a layout and a
  // passive effect, so splitting them shows the previous organization's
  // thread with the transport already re-scoped, and anything ordered
  // after this in the commit reads a conversation id belonging to the
  // organization that just went out of scope.
  //
  // Layout rather than render because a render can run twice under
  // StrictMode or be discarded before commit, which would leave the
  // transport scoped to an organization no committed tree is showing.
  useLayoutEffect(() => {
    setSidekickActingOrganizationId(actingOrganizationId);

    if (previousOrganizationId.current === actingOrganizationId) {
      return;
    }
    previousOrganizationId.current = actingOrganizationId;
    dispatch(resetChat());
  }, [actingOrganizationId, dispatch]);
};
